import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import css from './App.module.css';
import Sidebar from './components/02-sidebar/Sidebar';
import Dashboard from './components/03-main/dashboard/Dashboard';
import Student from './components/03-main/training/student/Student';
import Course from './components/03-main/training/course/Course';
import Header from './components/01_header/Header';
import Customer from './components/03-main/sales/customer/Customer';
import Contract from './components/03-main/sales/contract/Contract';
import Login from './components/03-main/_common/Login';
import PendingAccess from './components/03-main/_common/PendingAccess';
import Requests from './components/03-main/sales/request/Requests';
import Enroll from './components/03-main/training/enroll/Enroll';
import StudentOverview from './components/03-main/training/student/overview/StudentOverview';
import StudentDetails from './components/03-main/training/student/details/StudentDetails';
import CourseOverview from './components/03-main/training/course/overview/CourseOverview';
import CourseDetails from './components/03-main/training/course/details/CourseDetails';
import Program from './components/03-main/training/program/Program';
import ProgramOverview from './components/03-main/training/program/overview/ProgramOverview';
import ProgramDetails from './components/03-main/training/program/details/ProgramDetails';
import Compliance from './components/03-main/quaity/compliance/Compliance';
import AddComplianceItem from './components/03-main/quaity/compliance/AddComplianceItem';
import Staff from './components/03-main/quaity/staff/Staff';
import StaffOverview from './components/03-main/quaity/staff/overview/StaffOverview';
import StaffDetails from './components/03-main/quaity/staff/details/StaffDetails';
import ContractOverview from './components/03-main/sales/contract/ContractOverview';
import ContractDetails from './components/03-main/sales/contract/ContractDetails';
import Certificate from './components/03-main/training/certificate/Certificate';
import CertificateOverview from './components/03-main/training/certificate/overview/CertificateOverview';
import CertificateDetails from './components/03-main/training/certificate/details/CertificateDetails';
import CourseAttendance from './components/03-main/training/course/attendance/CourseAttendance';
import StudentList from './components/03-main/training/course/student-list/StudentList';
import CourseSchedule from './components/03-main/training/course/schedule/CourseSchedule';
import StaffCompetence from './components/03-main/quaity/staff/details/StaffCompetence';
import StaffExperience from './components/03-main/quaity/staff/experience/StaffExperience';
import Schedule from './components/03-main/sales/schedule/Schedule';
import CustomerOverview from './components/03-main/sales/customer/CustomerOverview';
import CustomerDetails from './components/03-main/sales/customer/CustomerDetails';
import CustomerContracts from './components/03-main/sales/customer/CustomerContracts';
import Invoice from './components/03-main/sales/invoice/Invoice';
import InvoiceOverview from './components/03-main/sales/invoice/InvoiceOverview';
import CertificateIssue from './components/03-main/training/certificate/issue/CertificateIssue';
import EasyAccess from './components/03-main/quaity/compliance/EasyAccess';
import Tnas from './components/03-main/training/tna/Tnas';
import TnaOverview from './components/03-main/training/tna/overview/TnaOverview';
import TnaModulesOverview from './components/03-main/training/tna/tna-modules/TnaModulesOverview';
import TnaModuleDetails from './components/03-main/training/tna/tna-modules/TnaModuleDetails';
import TnaDetails from './components/03-main/training/tna/overview/TnaDetails';
import TnaDifferencesOverview from './components/03-main/training/tna/differences/TnaDifferencesOverview';
import TnaDifferenceDetails from './components/03-main/training/tna/differences/TnaDifferenceDetails';
import * as authServices from './services/authServices';

function resolveAuthState(user) {
    return user.userRole === 'executive' ? 'executive' : 'pending';
}

function App() {
    const [authState, setAuthState] = useState('loading');
    const [user, setUser] = useState(null);

    useEffect(() => {
        const token = authServices.getToken();
        if (!token) {
            setAuthState('guest');
            return;
        }
        authServices.me()
            .then((sessionUser) => {
                setUser(sessionUser);
                setAuthState(resolveAuthState(sessionUser));
            })
            .catch(() => {
                authServices.clearToken();
                setUser(null);
                setAuthState('guest');
            });
    }, []);

    const handleAuthSuccess = (sessionUser) => {
        setUser(sessionUser);
        setAuthState(resolveAuthState(sessionUser));
    };

    const handleLogout = async () => {
        try {
            await authServices.logout();
        } catch {
            // still clear local session
        }
        authServices.clearToken();
        setUser(null);
        setAuthState('guest');
    };

    if (authState === 'loading') {
        return (
            <div className={css.loading}>
                <span>Loading…</span>
            </div>
        );
    }

    if (authState === 'guest') {
        return <Login onAuthSuccess={handleAuthSuccess} />;
    }

    if (authState === 'pending') {
        return <PendingAccess user={user} onLogout={handleLogout} />;
    }

    return (
        <div className={css.App}>
            <header className={css.Header}>
                <Header userEmail={user?.userEmail} onLogout={handleLogout} />
            </header>
            <BrowserRouter>
                <div className={css.Content}>
                    <nav className={css.Sidebar}>
                        <Sidebar />
                    </nav>
                    <main className={css.Main}>
                        <Routes>
                            <Route index element={<Dashboard />} />
                            <Route path="home" element={<Dashboard />} />
                            <Route path="schedule" element={<Schedule />} />

                            <Route path="customer" element={<Customer />}>
                                <Route index element={<CustomerOverview />} />
                                <Route path="overview" element={<CustomerOverview />} />
                                <Route path="details">
                                    <Route path=":customerId" element={<CustomerDetails />}>
                                        <Route path="contracts" element={<CustomerContracts />} />
                                    </Route>
                                </Route>
                            </Route>

                            <Route path="contract" element={<Contract />}>
                                <Route index element={<ContractOverview />} />
                                <Route path="overview" element={<ContractOverview />} />
                                <Route path="details" element={<ContractDetails />}>
                                    <Route path=":contractId" element={<ContractDetails />} />
                                </Route>
                            </Route>

                            <Route path="invoice" element={<Invoice />}>
                                <Route index element={<InvoiceOverview />} />
                                <Route path="overview" element={<InvoiceOverview />} />
                            </Route>

                            <Route path="request" element={<Requests />} />

                            <Route path="student" element={<Student />}>
                                <Route index element={<StudentOverview />} />
                                <Route path="overview" element={<StudentOverview />} />
                                <Route path="details" element={<StudentDetails />}>
                                    <Route path=":studentId" element={<StudentDetails />} />
                                </Route>
                            </Route>
                            <Route path="course" element={<Course />}>
                                <Route index element={<CourseOverview />} />
                                <Route path="overview" element={<CourseOverview />} />
                                <Route path="details">
                                    <Route path=":courseId" element={<CourseDetails />}>
                                        <Route path="student_list" element={<StudentList />} />
                                        <Route path="exam" />
                                        <Route path="attendance" element={<CourseAttendance />} />
                                        <Route path="schedule" element={<CourseSchedule />} />
                                    </Route>
                                </Route>
                            </Route>

                            <Route path="program" element={<Program />}>
                                <Route index element={<ProgramOverview />} />
                                <Route path="overview" element={<ProgramOverview />} />
                                <Route path="details" element={<ProgramDetails />}>
                                    <Route path=":programId" element={<ProgramDetails />} />
                                </Route>
                            </Route>

                            <Route path="tna" element={<Tnas />}>
                                <Route index element={<TnaOverview />} />
                                <Route path="overview" element={<TnaOverview />} />
                                <Route path="details/:tnaId" element={<TnaDetails />} />
                                <Route path="modules" element={<TnaModulesOverview />} />
                                <Route path="modules/details/:tnaModuleId" element={<TnaModuleDetails />} />
                                <Route path="differences" element={<TnaDifferencesOverview />} />
                                <Route path="difference/details/:id" element={<TnaDifferenceDetails />} />
                            </Route>

                            <Route path="enroll" element={<Enroll />}>
                                <Route path=":courseId" element={<Enroll />} />
                            </Route>

                            <Route path="certificate" element={<Certificate />}>
                                <Route index element={<CertificateIssue />} />
                                <Route path="overview" element={<CertificateOverview />} />
                                <Route path="issue" element={<CertificateIssue />} />
                                <Route path="details" element={<CertificateDetails />}>
                                    <Route path=":courseId" element={<CertificateDetails />} />
                                </Route>
                            </Route>

                            <Route path="compliance">
                                <Route index element={<Compliance />} />
                                <Route path="add" element={<AddComplianceItem />} />
                                <Route path="easy_access" element={<EasyAccess />} />
                            </Route>

                            <Route path="staff" element={<Staff />}>
                                <Route index element={<StaffOverview />} />
                                <Route path="overview" element={<StaffOverview />} />
                                <Route path="details">
                                    <Route path=":staffId" element={<StaffDetails />}>
                                        <Route path="staff_competence" element={<StaffCompetence />} />
                                        <Route path="staff_experience" element={<StaffExperience />} />
                                    </Route>
                                </Route>
                            </Route>
                        </Routes>
                    </main>
                </div>
            </BrowserRouter>
            <footer className={css.Footer}>
                <span>2025@PCA v.1.0.2.0 05072026</span>
            </footer>
        </div>
    );
}

export default App;

//2025@PCA v.1.0.0.1 19082025 - initial for version control
//2025@PCA v.1.0.1.1 19102025 - TNA modules and MCQ created
//2025@PCA v.1.0.2.0 05072026 - auth with roles (new / executive)
